import { useFragment, useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { DocumentType } from "generated-graphql/graphql";
import { useDocumentAI } from "hooks/useDocumentAI";

import {
  AddEditDocumentDialog,
  AddEditDocumentDialogProps,
  defaultAddEditDocumentDialogState,
  EditMode,
} from "../AddEditDocumentDialog";
import { AddEditDocumentWithAIEnrichmentDialog } from "../AddEditDocumentWithAIEnrichmentDialog";
import { ManifestDocumentDialog } from "../ManifestDocumentDialog";
import {
  ReviewDocumentMetadataDialog,
  ReviewDocumentMetadataDialogProps,
} from "../ReviewDocumentMetadataDialog";

type AddEditDialogPropOverrides = Omit<AddEditDocumentDialogProps, "tenantId">;

type ReviewDialogPropOverrides = Omit<ReviewDocumentMetadataDialogProps, "tenantId">;

type DocumentDialogProps = {
  tenantId?: string;
  // Review dialog props (when provided, shows review dialog)
  reviewDialogProps?: ReviewDialogPropOverrides;
  // Add/Edit dialog props (when reviewDialogProps is not provided)
  addEditDialogProps: AddEditDialogPropOverrides;
};

// Manifest documents are managed differently from general documents: the general
// AI-metadata review form doesn't apply to them, and their review lifecycle lives
// in the Waste module.
//
// Routing signal: `Document.documentType`. This is the reliable, creation-time
// document classification (distinct from `DocumentMetadata.type`, which several
// pipelines write inconsistently). Every document grid already selects it, so we
// can read it synchronously from the cache and route on the first render with no
// flash. For deep links — where the document isn't in any list cache — we fall
// back to the authoritative `sourceWasteManifest` network check.
const MANIFEST_DOCUMENT_TYPES: ReadonlySet<DocumentType> = new Set([
  DocumentType.RcraManifest,
  DocumentType.RcraManifestPrintedCopy,
]);

export const DOCUMENT_ROUTING_TYPE = gql(`
  fragment DocumentRoutingType on Document {
    id
    documentType
  }
`);

export const DOCUMENT_MANIFEST_LINK = gql(`
  query DocumentManifestLink($id: ID!) {
    document(id: $id) {
      id
      sourceWasteManifest {
        id
      }
    }
  }
`);

export function DocumentDialog(props: DocumentDialogProps) {
  const { tenantId, reviewDialogProps, addEditDialogProps } = props;

  const hasDocumentAI = useDocumentAI();

  const reviewDocumentIds = reviewDialogProps?.documentIds ?? [];
  const isReviewOpen = !!reviewDialogProps?.open;

  // Only single-document opens (deep links, row clicks) route to the manifest
  // dialog. Multi-document bulk review stays on the general dialog; the server
  // guard still protects any manifest docs caught up in a bulk save.
  const manifestCheckId =
    isReviewOpen && reviewDocumentIds.length === 1 ? reviewDocumentIds[0] : undefined;

  // Synchronous cache read of the document's type — populated by the grid that
  // opened this dialog, so list-originated opens route with no flash.
  const { data: cachedDoc, complete: typeIsCached } = useFragment({
    fragment: DOCUMENT_ROUTING_TYPE,
    from: manifestCheckId ? `Document:${manifestCheckId}` : "",
  });
  const isManifestByType =
    typeIsCached && !!cachedDoc.documentType && MANIFEST_DOCUMENT_TYPES.has(cachedDoc.documentType);

  // Only hit the network when the type isn't already cached (deep links). On
  // error, fall through to the general dialog rather than blocking the open.
  const { data: manifestLink } = useQuery(DOCUMENT_MANIFEST_LINK, {
    variables: { id: manifestCheckId ?? "" },
    skip: !manifestCheckId || typeIsCached,
    errorPolicy: "all",
  });

  const isManifestDocument = isManifestByType || !!manifestLink?.document?.sourceWasteManifest?.id;

  if (!tenantId) {
    return null;
  }

  // Handle review dialog mode
  if (hasDocumentAI && reviewDialogProps?.open) {
    // Render the general review dialog by default and only swap to the manifest
    // dialog once a manifest is positively confirmed. We deliberately never
    // return null here while the check is in flight: unmounting the review
    // dialog mid-open churns its URL/selection effects and prevents non-manifest
    // documents from opening at all.
    if (isManifestDocument && manifestCheckId) {
      return (
        <ManifestDocumentDialog
          documentId={manifestCheckId}
          tenantId={tenantId}
          onClose={reviewDialogProps.onClose}
          onComplete={reviewDialogProps.onComplete}
        />
      );
    }

    return (
      <ReviewDocumentMetadataDialog
        tenantId={tenantId}
        hasDocumentAI={hasDocumentAI}
        {...reviewDialogProps}
      />
    );
  }

  if (hasDocumentAI && addEditDialogProps?.dialogState?.mode === EditMode.ADD) {
    return (
      <AddEditDocumentWithAIEnrichmentDialog
        open={addEditDialogProps?.dialogState?.open}
        tenantId={tenantId}
        onClose={() => {
          addEditDialogProps?.setDialogState?.(defaultAddEditDocumentDialogState);
        }}
      />
    );
  }

  return <AddEditDocumentDialog tenantId={tenantId} {...addEditDialogProps} />;
}
